import { Box, Card, CardMedia, Typography, IconButton } from "@mui/material"
import { createTheme, styled } from "@mui/material/styles"

export const theme = createTheme({
  palette: {
    primary: {
      main: "#3D3B5C",
    },
    secondary: {
      main: "#5A5880",
    },
    background: {
      default: "#ffffff",
      paper: "#f8fafc",
    },
    text: {
      primary: "#2A2940",
      secondary: "#3D3B5C",
    },
  },
  typography: {
    fontFamily: "var(--font-sans), system-ui, sans-serif",
    h4: {
      fontWeight: 700,
      color: "#3D3B5C",
      letterSpacing: "-0.02em",
    },
    body2: {
      color: "#3D3B5C",
    },
  },
  shape: {
    borderRadius: 8,
  },
})

export const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  backgroundColor: theme.palette.background.default,
  paddingBottom: theme.spacing(6),
}))

export const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(1),
}))

export const DesktopLogo = styled("img")(({ theme }) => ({
  height: 30,
  width: "auto",
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}))

export const MobileLogo = styled("img")(({ theme }) => ({
  height: 40,
  width: "auto",
  [theme.breakpoints.up("sm")]: {
    display: "none",
  },
}))

export const InfoCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 1px 3px rgba(61, 59, 92, 0.1)",
  borderRadius: theme.shape.borderRadius * 2,
}))

export const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
  textAlign: "center",
}))

export const DescriptionText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  textAlign: "center",
  fontSize: "1rem",
}))

export const StatContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
})

export const StatBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  minWidth: "100px",
})

export const StatValue = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
}))

export const StatLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
}))

export const SwatchesGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  justifyContent: "center",
  alignItems: "flex-start",
  marginTop: theme.spacing(2),
}))

export const SwatchCard = styled(Card)(({ theme }) => ({
  width: "280px",
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 1px 3px rgba(61, 59, 92, 0.1)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 25px rgba(61, 59, 92, 0.15)",
  },
}))

export const SwatchTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: "1.1rem",
  lineHeight: 1.3,
}))

export const SwatchMediaContainer = styled(Box)({
  position: "relative",
  overflow: "hidden",
})

export const SwatchMedia = styled(CardMedia)({
  transition: "transform 0.3s ease",
  objectFit: "cover",
  "&:hover": {
    transform: "scale(1.02)",
  },
})

export const GalleryIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  color: theme.palette.text.secondary,
  backdropFilter: "blur(4px)",
  transition: "all 0.2s ease",
  padding: "6px",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 1)",
    color: "#5A5880",
    transform: "scale(1.1)",
  },
}))

export const GalleryButtonContainer = styled(Box)({
  position: "absolute",
  top: 8,
  right: 8,
  display: "flex",
  gap: 8,
})

export const CopyrightContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6),
  paddingTop: theme.spacing(3),
  borderTop: "1px solid rgba(61, 59, 92, 0.1)",
  textAlign: "center",
}))

export const CopyrightText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}))

export const DisclaimerText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "block",
  maxWidth: "500px",
  marginLeft: "auto",
  marginRight: "auto",
  lineHeight: 1.5,
}))

export const ModalImageContainer = styled(Box)({
  position: "relative",
  textAlign: "center",
})

export const ModalImage = styled("img")({
  width: "100%",
  height: "70vh",
  objectFit: "contain",
  borderRadius: 4,
})

export const CarouselPrevButton = styled(IconButton)({
  position: "absolute",
  left: 8,
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
})

export const CarouselNextButton = styled(IconButton)({
  position: "absolute",
  right: 8,
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
})

export const CenterMessageContainer = styled(Box)({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
})
